import axios from "axios";
import AzureAiClientBase, { TAzureAiClientConstructor } from "./ai.base.client";
import { Message } from "../ai.types";
import { SearchFn, SearchRequest } from "../../../search/search";
import { ESApiFinalResponseInterface } from "../../../interfaces/ElasticSearchInterface";
import { getFrom } from "../../../util/map";
import { defaultSearchContext } from "../../../search/searchContext";
import { knnSearch, knnSearchWithKeyword } from "../../../search/all.searchs";
import { AiActionByCustomType, AiActionByType } from "../ai.actions";
import { getDecisionTree } from "../../ai-assist-refactored/quick-actions/decisionTree";
import { QuickActionType } from "../../../modules/AiWidgets/widget.types";
import aiPromptClassifier from "../actions/ai.promptClassifier";
import mapSearchResultToDocument, { getDefaultTextForHr } from "../actions/ai.mapSearchResultToDocument";
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from "react";
import { jiraSearch } from "../../../search/jql.search";
import { fallbackldsl, nexthinkPrompt, nextthinkdsl } from "../../../nextthink/ai.prompt.nextthink";
import { openAiClientForNextthink } from "../../../nextthink/ai/ai.client.for.nextthink";
import React from "react";
import { DebugLog } from "@me8eon/debug";
import { fetchDeviceDetails } from "@me8eon/ai.tools.nextthink";
import { aiConfig, jqlAiClient } from "../../../index";
import { JwtTokenFn } from "@me8eon/authentication";
import { indexToNameMap } from "../../ai-assist-refactored/hooks/useAiData";
import {
    getRagPromptWithPersonalizedDocuments,
    getRagPromptWithPersonalizedDocumentsFF_1186,
    getRagPromptWithPersonalizedDocumentsFF_1139,
    getRagPromptWithPersonalizedDocuments_reranked_answer,
    getHrPolicyPrompt,
    getTranslationPrompt,
    languageDetectionPrompt,
    supportedLanguages,
    supportedLanguagesType
} from "../prompts/ai.prompts.ragPersonalized";
import { getSearchIndicesArray } from "../indices/indices.helpers";
import { OutputAndSource } from "../contexts/ai.rag.context";
import { SourceNameWithIndices } from "../indices/indices.types";
import { teamsPrompt } from "../../../teams/ai.teamsprompt";
import { openAiClientForTeams } from "../../../teams/ai/aiclientforteams";
import { FeatureFlags } from "@me8eon/featureflags/src/feature.flags";
import { EnvironmentConfig, LLM_MODEL } from "@me8eon/config";


export function getFeedbackDataFromSource(indexNames: Array<string>) {
    const displayNamesArr = indexNames.map((index) => {
        const machedData = Object.keys(indexToNameMap).filter((indexObj) => index.match(indexObj));
        const machedTitle = indexToNameMap[machedData[0]];
        return machedTitle;
    });
    const displayNamesArrUnique = [...new Set(displayNamesArr)].filter((name) => name).join(", ");
    return displayNamesArrUnique;
}

interface GptResponse {
    category: string;
    response: string;
}

const cleanHrDocumentContent = (content: string): string => {
    try {
        return content
            .replace(/HR Document \d+:\s*/g, "")
            .replace(/\*\*Context\*\*:.*?\n/g, "")
            .replace(/\*\*Kontext\*\*:.*?\n/g, "")
            .replace(/\*\*/g, "")
            .replace(/\([^)]*\.(docx|pdf)[^)]*\)/gi, "")
            .replace(/Document title:.*$/gim, "")
            .replace(/Dokumenttitel:.*$/gim, "")
            .replace(/Document URL:.*$/gim, "")
            .replace(/Dokument-URL:.*$/gim, "")
            .replace(/Weitere Einzelheiten sind im Dokument mit dem Titel.*?\.pdf/gi, "")
            .replace(/weitere Details finden Sie im Dokument mit dem Titel.*?\.pdf/gi, "")
            .replace(/verfügbar unter:.*?\.pdf/gi, "")
            .replace(/available at:.*?\.pdf/gi, "")
            .replace(/'20250901 Final OnePager Full-Time Plus\.pdf'/gi, "")
            .replace(/\(20250901 Final OnePager Full-Time Plus\.pdf\)/gi, "")
            .trim();
    } catch (error) {
        console.error('Error cleaning HR document:', error);
        return content || '';
    }
};


export const parseAndFixJson = (raw: string, fallback: any = {}): any => {
    if (!raw) return fallback;
    let parsed: any = null;
    try {
        parsed = JSON.parse(raw);
    } catch (parseError) {
        console.warn(" Invalid JSON detected, attempting to auto-fix...", parseError);
        try {
            raw = raw.replace(/([{,])\s*([a-zA-Z0-9_]+)\s*:/g, '$1"$2":');
            raw = raw.replace(/,\s*([}\]])/g, '$1');

            parsed = JSON.parse(raw);
        } catch (fixError) {
            console.error(" Failed to fix and parse JSON:", fixError);
            parsed = fallback;
        }
    }

    return parsed ?? fallback;
};



const processHrDocuments = (jointData: any[]): { content: string }[] => {
    return jointData
        .filter((doc): doc is NonNullable<typeof doc> =>
            doc?.index === EnvironmentConfig.semanticHrIndex
        )
        .map(searchResult => {
            let content = getDefaultTextForHr(searchResult);
            content = cleanHrDocumentContent(content);
            return { content };
        })
        .filter((doc): doc is { content: string } =>
            doc !== null && doc.content.length > 0
        );
};

type AiRagConfig = {
    searchFn: SearchFn<ESApiFinalResponseInterface>;
    makeOutputAndSource: (
        searchResultId: Array<string>,
        messages: Message[],
    ) => OutputAndSource;
};

export function defaultAiRagConfig(
    searchFn: SearchFn<ESApiFinalResponseInterface>,
): AiRagConfig {
    return {
        searchFn,
        makeOutputAndSource: (
            searchResultId: Array<string>,
            messages: Message[],
            data?: any,
        ): OutputAndSource => {
            return ({
                output: messages[0].content,
                source: getFeedbackDataFromSource(searchResultId),
                action: "rag",
                data,
            });
        },
    };
}


export interface IAiRagClient {
    config: TAzureAiClientConstructor;
    rememberSearchOps: GetterSetter<ESApiFinalResponseInterface[]>;
    searchEmbeddingModel: string,
    debug: DebugLog;
    companyId: string;
}


export type Setter<T> = Dispatch<SetStateAction<T>>
export type GetterSetter<T> = [T, Setter<T>]
export const AiSourceDataMemoryContext = createContext<GetterSetter<ESApiFinalResponseInterface[]> | undefined>(undefined);

export function AiSourceDataMemoryProvider({ children }: { children: ReactNode }) {
    const ops: GetterSetter<ESApiFinalResponseInterface[]> = useState<ESApiFinalResponseInterface[]>([]);
    return <AiSourceDataMemoryContext.Provider value={ops}>{children}</AiSourceDataMemoryContext.Provider>;
}

export function useAiSourceMemory() {
    const context = useContext(AiSourceDataMemoryContext);
    if (!context) throw new Error("useAiSourceDataMemory must be used within AiSourceDataMemoryProvider");
    return context;
}

export const aiRagDebug = "aiRagDebug";

export class AiRagClient extends AzureAiClientBase {
    protected elasticApiKey: string | undefined;
    private readonly searchContext: ReturnType<typeof defaultSearchContext>;
    private aiRagConfig: AiRagConfig;
    setRememberSearch: Setter<ESApiFinalResponseInterface[]>;
    private debug: DebugLog;

    constructor({ config, rememberSearchOps, debug, searchEmbeddingModel, companyId }: IAiRagClient) {
        super(config);
        this.debug = debug;
        this.searchContext = { ...defaultSearchContext(axios), embeddingModel: searchEmbeddingModel }
        this.setRememberSearch = rememberSearchOps[1];
        this.aiRagConfig = {
            ...defaultAiRagConfig(
                knnSearchWithKeyword(this.searchContext, companyId),
            ),
        };
    }

    public async aiClientQuickActions(history: Message[], query: string, quickActionType: QuickActionType, data: any) {
        const lastMessageContent = history[history.length - 1]?.content;
        const category = lastMessageContent ? "ongoing" : "new";
        let findNode = getDecisionTree().find(node => node.userCommand === query && (!data?.nodeId || data?.nodeId === node.id) && (!node.condition || node.condition({
            category,
            quickActionType
        })));
        findNode = { ...findNode, ...data || {} };
        return getFrom(AiActionByCustomType)("quick-actions")(findNode);
    }

    public async handleQuery(history: Message[], query: string, language: string, esApiKey: JwtTokenFn, sourcesWithSearchIndices: SourceNameWithIndices, personalizedSourcesWithSearchIndices: SourceNameWithIndices, featureFlagEnabled?: boolean, ff?: FeatureFlags) {
        const result = await aiPromptClassifier(query, featureFlagEnabled);
        const { classificationResult, translated_query, original_query } = result
        const sendWithPreviousHistory = (messages: Message[], otherConfig?: any) => this.sendRequest([...history, ...messages], otherConfig);

        const combinedSourcesWithSearchIndices = new Map([...sourcesWithSearchIndices, ...personalizedSourcesWithSearchIndices]);

        const searchQuery = (indicies: string[]) => this.aiRagConfig.searchFn({
            dataType: "assistance",
            apiKey: esApiKey,
            searchTerm: translated_query,
            searchIndexes: indicies,
        });

        const classificationMap = {
            "action": async () => {
                const documents = await searchQuery(["actions-prod"]);
                this.setRememberSearch(documents.data);
                return await getFrom(AiActionByType)(documents.data[0].action)(sendWithPreviousHistory);
            },
            "rag": async (t_query: string) => {
                const hasPersonalizedIndices = getSearchIndicesArray(personalizedSourcesWithSearchIndices).length > 0;
                const searchResults = await searchQuery(getSearchIndicesArray(sourcesWithSearchIndices));
                const firstThreeSearchResults = searchResults.data.slice(0, 3);
                const mappedTextFromDocuments = firstThreeSearchResults.map(mapSearchResultToDocument(combinedSourcesWithSearchIndices));
                const personalizedIndiciesSearchResults = hasPersonalizedIndices ? await searchQuery(getSearchIndicesArray(personalizedSourcesWithSearchIndices)) : { data: [] };
                const firstThreePersonalizedSearchResults = personalizedIndiciesSearchResults.data.slice(0, 3);
                const mappedTextFromPersonalizedDocuments = firstThreePersonalizedSearchResults.map(mapSearchResultToDocument(combinedSourcesWithSearchIndices));

                const jointData = [...firstThreeSearchResults, ...firstThreePersonalizedSearchResults];
                this.setRememberSearch(jointData);
                const uniqueIndexArr = [...new Map(jointData.map(item => [item.index, item])).keys()];
                const hrDocsCount = jointData.filter(item => item.index === EnvironmentConfig.semanticHrIndex).length;
                const isSemanticHr = hrDocsCount >= 2;
                this.debug(aiRagDebug, "rag", firstThreeSearchResults, firstThreePersonalizedSearchResults);
                let aiPrompt;
                const userLanguage = await this.sendRequest([
                    { role: "user", content: languageDetectionPrompt(query) }
                ])
                let responseLanguage = userLanguage?.[userLanguage.length - 1]?.content?.trim();
                responseLanguage = ['german', 'swedish', 'english', 'romanian'].includes(responseLanguage.toLowerCase()) ? responseLanguage : supportedLanguages[language as supportedLanguagesType];
                if (isSemanticHr) {
                    try {
                        const hrDocuments = processHrDocuments(jointData);
                        if (hrDocuments.length > 0) {
                            aiPrompt = getHrPolicyPrompt(hrDocuments, language, original_query ?? translated_query ?? "");
                        } else {
                            throw new Error('No HR documents found');
                        }
                    } catch (error) {
                        console.warn('HR processing failed, using fallback RAG:', error);
                        aiPrompt = getRagPromptWithPersonalizedDocumentsFF_1139(
                            mappedTextFromDocuments,
                            mappedTextFromPersonalizedDocuments,
                            language,
                            responseLanguage
                        );
                    }
                } else if (window.location.href.includes("me8eon1186=true")) {
                    aiPrompt = getRagPromptWithPersonalizedDocumentsFF_1186(mappedTextFromDocuments, mappedTextFromPersonalizedDocuments, language);
                } else {
                    aiPrompt = getRagPromptWithPersonalizedDocumentsFF_1139(mappedTextFromDocuments, mappedTextFromPersonalizedDocuments, language, responseLanguage);
                }
                //reverted changes from adding_chat_history
                const message = [
                    { role: "system", content: aiPrompt },
                    { role: "user", content: query },
                ] as Message[];

                const outputMessages = await sendWithPreviousHistory(message, { model: LLM_MODEL });
                if (isSemanticHr) {
                    const hrExcerpt = outputMessages?.[outputMessages.length - 1]?.content?.trim();
                    const translationPrompt = getTranslationPrompt(original_query ?? "", hrExcerpt, language);
                    const translationMessages: Message[] = [
                        { role: "system", content: translationPrompt },
                        { role: "user", content: hrExcerpt ?? "" },
                    ];
                    const translationOutput = await sendWithPreviousHistory(translationMessages, { model: LLM_MODEL });
                    const finalAnswer = translationOutput?.[translationOutput.length - 1]?.content?.trim();
                    return {
                        ...this.aiRagConfig.makeOutputAndSource(uniqueIndexArr, [
                            { role: "assistant", content: finalAnswer },
                        ]),
                    };
                }
                return this.aiRagConfig.makeOutputAndSource(uniqueIndexArr, outputMessages);
            },
            "jira": async () => {
                const searchFn = jiraSearch(jqlAiClient);
                const searchRequest: SearchRequest = {
                    apiKey: esApiKey,
                    searchTerm: query,
                    dataType: "jql",
                    searchIndexes: ["jql"],
                };

                const { data, jql, totalCount, error, errorMsg } = await searchFn(searchRequest);
                const topData = data.slice(0, 15);
                //We don't record the topTenData because they are outputs from the process, and the setRememberSearch is recording the inputs
                //The purpose of setRememberSearch is to allow us to improve the quality of the documents, and
                //storing these would just confuse that issue
                this.setRememberSearch([]);
                this.debug(aiRagDebug, "jira", []);
                return {
                    output: "Following are the results for your query",
                    source: "jql",
                    action: "jql-query",
                    data: { data: topData, jql, totalCount, error, errorMsg },
                };
            },
            "nextthink": async (t_query: string) => {
                const searchResults: any = await searchQuery([EnvironmentConfig.nexthinkIndex]);
                const topResult: any = searchResults?.data?.[0] || null;
                const categoryFromElastic: string = topResult?.category || "nexthink-smartsolution";
                const aiPrompt = `${nexthinkPrompt}
                
                Output:`;

                const messages: any[] = [
                    { role: "system", content: aiPrompt },
                    { role: "user", content: query },
                ];

                const nextthinkAiClient: any = openAiClientForNextthink(aiConfig);
                const outputMessages: any = await nextthinkAiClient(messages);
                let lastContent: string | undefined = outputMessages?.[outputMessages.length - 1]?.content?.trim();
                let gptResponse: any = null;
                try {
                    gptResponse = parseAndFixJson(lastContent || "", {
                        category: categoryFromElastic,
                        response: "Unable to classify accurately",
                    });
                } catch (err) {
                    console.error(" [NexThink] Unexpected error parsing GPT response:", err);
                    gptResponse = {
                        category: categoryFromElastic,
                        response: "Unable to classify accurately",
                    };
                }
                const ragresponse = await classificationMap["rag"](t_query);
                const deviceDetails = await fetchDeviceDetails(esApiKey);

                const action = ["sustainable-it", "nexthink-smartsolution"].includes(gptResponse?.category || "") ? gptResponse?.category : "nexthink";
                return {
                    output: gptResponse?.response ?? "",
                    source: "nexthink",
                    action,
                    data: {
                        category: gptResponse?.category ?? "",
                        rag: ragresponse.output ?? null,
                        deviceDetails,
                        source: ragresponse.source ?? null,
                    },
                };
            },

            "teams": async (t_query: string) => {
                const searchResults: any = await searchQuery([EnvironmentConfig.nexthinkTeamsIndex]);
                const topResult: any = searchResults?.data?.[0] || null;
                const categoryFromElastic: string = topResult?.category;

                const aiPrompt = teamsPrompt(categoryFromElastic, query, language);
                const messages: Message[] = [
                    { role: "system", content: aiPrompt },
                    { role: "user", content: query },
                ];

                const teamsAiClient = openAiClientForTeams(aiConfig);
                const outputMessages = await teamsAiClient(messages);
                let lastContent = outputMessages?.[outputMessages.length - 1]?.content?.trim();
                let gptResponse: GptResponse | null = null;

                try {
                    if (lastContent) {
                        try {
                            gptResponse = JSON.parse(lastContent);
                        } catch (parseError) {
                            try {
                                console.warn("Invalid JSON detected, attempting to fix...");
                                lastContent = lastContent.replace(
                                    /([{,])\s*([a-zA-Z0-9_]+)\s*:/g,
                                    '$1"$2":',
                                );
                                lastContent = lastContent.replace(/,\s*([}\]])/g, "$1");
                                gptResponse = JSON.parse(lastContent);
                            } catch (fixError) {
                                console.error("Failed to fix and parse GPT response:", fixError);
                                gptResponse = null;
                            }
                        }
                    }
                } catch (error) {
                    console.error("Unexpected error parsing GPT response:", error);
                }

                const ragresponse = await classificationMap["rag"](t_query);
                const deviceDetails = await fetchDeviceDetails(esApiKey);

                return {
                    output: gptResponse?.response ?? "",
                    source: "teams",
                    action: "teams",
                    data: {
                        category: gptResponse?.category ?? "",
                        rag: ragresponse.output ?? null,
                        deviceDetails,
                        source: ragresponse.source ?? null,
                    },
                };
            },

            default: async () => {
                this.setRememberSearch([]);
                this.debug(aiRagDebug, "default", []);
                throw new Error(`Classification result is not supported`);
            },
        };

        return getFrom(classificationMap)(await classificationResult)(translated_query);
    }
}